import { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { LeadMagnetApi } from "../../service";
import Logo from "../assets/Logo.png";
import LogoFull from "../assets/LogoFull.png";
import HeroBg from "../assets/Home/LandingPageBg.png";
import CheckCircle from "../assets/Home/CheckCircle.svg";
import BookMockup from "../assets/Home/BookMockup.png";
import "./LeadMagnet.css";

const schema = Yup.object({
  email: Yup.string().email("Please enter a valid email").required("Email is required"),
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Last name is required"),
});

function CheckIcon() {
  return (
    <img
      src={CheckCircle}
      alt=""
      className="w-5 h-5 flex-shrink-0 mt-0.5"
      style={{ filter: "invert(58%) sepia(80%) saturate(400%) hue-rotate(90deg) brightness(95%)" }}
    />
  );
}

export default function LeadMagnetPage() {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();

  // Prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex,nofollow";
    document.head.appendChild(meta);
    return () => document.head.removeChild(meta);
  }, []);

  useEffect(() => {
    LeadMagnetApi.getPage()
      .then((data) => setPageContent(data))
      .catch(() => setFetchError("Unable to load page content. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (values, { setSubmitting, setFieldError }) => {
    try {
      await LeadMagnetApi.submit(values);
      navigate("/free-ebook/thank-you");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.email) setFieldError("email", Array.isArray(data.email) ? data.email[0] : data.email);
      else if (data?.first_name) setFieldError("first_name", Array.isArray(data.first_name) ? data.first_name[0] : data.first_name);
      else if (data?.last_name) setFieldError("last_name", Array.isArray(data.last_name) ? data.last_name[0] : data.last_name);
      else setFieldError("email", "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const defaultBullets = [
    "How to identify hidden home issues before they become major repairs.",
    "Practical ways to protect your family's health from common household hazards.",
    "What Long Island homeowners should know about roofs, foundations, plumbing, electrical systems, and more.",
    "Warning signs of costly problems that most homeowners overlook.",
    "Simple maintenance habits that can save thousands in future repair costs.",
    "A homeowner's roadmap for preventing emergencies, avoiding unnecessary expenses, and maintaining property value.",
  ];

  const apiBullets = pageContent
    ? [
        pageContent.bullet1,
        pageContent.bullet2,
        pageContent.bullet3,
        pageContent.bullet4,
        pageContent.bullet5,
        pageContent.bullet6,
      ].filter(Boolean)
    : [];

  const bullets = apiBullets.length > 0 ? apiBullets : defaultBullets;

  const leftBullets = bullets.slice(0, 3);
  const rightBullets = bullets.slice(3, 6);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="w-10 h-10 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <p className="text-white text-lg">{fetchError}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col font-sans">
      {/* ── Header Bar ── */}
      <header className="bg-white border-b border-gray-200 py-3 px-6 flex justify-center">
        <img src={LogoFull} alt="PropertEasy" className="h-14 w-auto" />
      </header>

      {/* ── Hero Section ── */}
      <section
        className="relative flex items-center min-h-[calc(100vh-82px)] pb-12 px-4"
        style={{
          backgroundImage: `url(${HeroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {/* Dark overlay */}
        <div
          className="absolute inset-0"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.70)" }}
        />

        <div className="relative z-10 max-w-6xl mx-auto w-full" style={{ fontFamily: "'Inter', sans-serif" }}>
          <h1
            className="text-[40px] md:text-[52px] lg:text-[60px] font-bold text-white mb-12 text-center uppercase"
            style={{ fontFamily: "'DM Sans', sans-serif", fontSize: "60px", fontWeight: 700, lineHeight: 1.0 }}
          >
            {pageContent?.title || "10 HIDDEN DANGERS IN YOUR HOME"}
          </h1>

          <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left column — headline + form */}
            <div className="flex-1 w-full lg:max-w-[55%]">
              <div className="flex flex-col items-center text-center mb-12">
                <p
                  className="mb-5"
                  style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "24px", fontWeight: 400, lineHeight: 1.2 }}
                >
                  <span style={{ color: "#52C271" }}>5</span> <span className="text-white">Threats to Your</span> <span style={{ color: "#52C271" }}>Health.</span>
                </p>
                <p
                  className="mb-8"
                  style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "24px", fontWeight: 400, lineHeight: 1.2 }}
                >
                  <span style={{ color: "#52C271" }}>5</span> <span className="text-white">Threats to Your</span> <span style={{ color: "#52C271" }}>Wallet.</span>
                </p>
                <p
                  className="text-white opacity-90"
                  style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif", fontSize: "24px", fontWeight: 400, lineHeight: 1.65, maxWidth: "400px" }}
                >
                  {pageContent?.subtitleLine3 || "And How Early Detection and Routine Maintenance Can Prevent Both"}
                </p>
              </div>

              <Formik
                initialValues={{ email: "", first_name: "", last_name: "" }}
                validationSchema={schema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-3 max-w-md mx-auto">
                    {/* Email */}
                    <div>
                      <Field
                        name="email"
                        type="email"
                        placeholder="Email Address"
                        className="w-full bg-white border-0 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 tracking-wider"
                        style={{ fontSize: "13px", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                      />
                      <ErrorMessage name="email" component="p" className="text-red-400 text-xs mt-1" />
                    </div>

                    {/* First Name */}
                    <div>
                      <Field
                        name="first_name"
                        type="text"
                        placeholder="First Name"
                        className="w-full bg-white border-0 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 tracking-wider"
                        style={{ fontSize: "13px", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                      />
                      <ErrorMessage name="first_name" component="p" className="text-red-400 text-xs mt-1" />
                    </div>

                    {/* Last Name */}
                    <div>
                      <Field
                        name="last_name"
                        type="text"
                        placeholder="Last Name"
                        className="w-full bg-white border-0 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-green-500 tracking-wider"
                        style={{ fontSize: "13px", fontFamily: "'Inter', sans-serif", fontWeight: 500 }}
                      />
                      <ErrorMessage name="last_name" component="p" className="text-red-400 text-xs mt-1" />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full text-white font-semibold py-3 rounded tracking-wider transition-opacity disabled:opacity-60"
                      style={{ backgroundColor: "#52C271", fontSize: "14px", fontFamily: "'Inter', sans-serif" }}
                    >
                      {isSubmitting
                        ? "Sending..."
                        : pageContent?.ctaButtonText || "Continue to Download"}
                    </button>

                    <p className="text-center text-white" style={{ fontSize: "11px" }}>
                      Your email address will be safely managed according to our{" "}
                      <a
                        href="https://properteasy.com/legal"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:opacity-80"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  </Form>
                )}
              </Formik>
            </div>

            {/* Right column — eBook mockup */}
            <div className="flex-1 flex justify-center lg:justify-end lg:max-w-[45%]">
              <img
                src={pageContent?.bookMockupImageUrl || BookMockup}
                alt="Free eBook"
                className="max-w-xs lg:max-w-md w-full drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── What's Inside Section ── */}
      <section className="bg-white py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-center text-2xl md:text-3xl font-semibold text-gray-800 mb-14">
            {pageContent?.sectionTitle || "A Long Island Property Owner's Guide"}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            <div className="space-y-8">
              {leftBullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-4">
                  <CheckIcon />
                  <p className="text-gray-600 text-sm leading-relaxed">{bullet}</p>
                </div>
              ))}
            </div>
            <div className="space-y-8">
              {rightBullets.map((bullet, i) => (
                <div key={i} className="flex items-start gap-4">
                  <CheckIcon />
                  <p className="text-gray-600 text-sm leading-relaxed">{bullet}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Green CTA Banner ── */}
      <section className="py-14 px-6" style={{ backgroundColor: "#52C271" }}>
        <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center justify-center gap-6">
          <div className="text-white text-center max-w-sm">
            <h3 className="text-xl font-bold mb-2">
              {pageContent?.bannerHeadline || "Know what to look for. Know who to call."}
            </h3>
            <p className="text-white text-sm">
              {pageContent?.bannerSubtext ||
                "Skip the phone calls and guesswork. Submit your job and we'll send you the right professional."}
            </p>
          </div>
          <button
            onClick={() => navigate("/post-job")}
            className="bg-white font-semibold px-16 py-4 rounded text-base whitespace-nowrap flex-shrink-0"
            style={{ color: "#1a1a1a" }}
          >
            {pageContent?.bannerButtonText || "Post a Job"}
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-white border-t border-gray-100 py-6 px-6">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-3">
          <img src={Logo} alt="PropertEasy" className="h-7 w-auto" />
          <p className="text-gray-400 text-xs text-center">
            &copy;2026 Properteasy. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
